import React, { useCallback, useRef } from "react";
import { toast } from "sonner";

/**
 * Anti-casual-copy wrapper for PAID / educational study content ONLY.
 * - Disables text selection via the `.protected-study-content` CSS class.
 * - Blocks copy / cut / drag / right-click (context menu) on the wrapped content.
 * Does NOT block clicks, keyboard navigation, answer selection, form entry, or
 * screen readers. Inputs/buttons inside stay usable (CSS re-enables select on inputs).
 *
 * Honest limitation: this reduces easy copying but cannot prevent screenshots,
 * photos, screen recording, or manual retyping.
 */
export default function ProtectedContent({
  as: Tag = "div",
  className = "",
  children,
  ...rest
}) {
  const lastNotifyRef = useRef(0);

  const notify = useCallback(() => {
    const now = Date.now();
    if (now - lastNotifyRef.current < 1500) return; // debounce — non-intrusive
    lastNotifyRef.current = now;
    toast.message("Study content is protected and not available for copying.", {
      duration: 2000,
    });
  }, []);

  const blockAndNotify = useCallback(
    (e) => {
      e.preventDefault();
      notify();
    },
    [notify]
  );

  const blockSilently = useCallback((e) => {
    e.preventDefault();
  }, []);

  return (
    <Tag
      className={["protected-study-content", className].filter(Boolean).join(" ")}
      onCopy={blockAndNotify}
      onCut={blockAndNotify}
      onDragStart={blockSilently}
      onContextMenu={blockSilently}
      {...rest}
    >
      {children}
    </Tag>
  );
}
